/**
 * Effect-native mutation atom — 取代 useMutationRunner 的 Promise 生命周期。
 *
 * 模型（Atom.fn 语义）：
 * - mutation 状态是 `Atom<AsyncResult<Success, Error>>`，写入 Effect 即执行
 * - execute 接收 `Effect<Success, Error>`；错误类型保留（不擦除为 unknown）
 * - 重复提交策略显式化：运行中 drop（返回 typed Dropped），绝不悬挂
 * - React 侧用 useAtomValue/useAtomSet 直接消费 command.state
 *
 * registry：命令私有 AtomRegistry（mount 保持求值）。失效接线（reactivityKeys）
 * 在命令描述符层声明，组件不再手写跨域 bump。
 */

import { Cause, Effect, Exit, Layer, Scope } from "effect";
import * as Atom from "effect/unstable/reactivity/Atom";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import * as AtomRegistry from "effect/unstable/reactivity/AtomRegistry";
import type * as Reactivity from "effect/unstable/reactivity/Reactivity";

/** Mutation 的显式状态代数（AsyncResult：Initial → Waiting → Success/Failure） */
export type MutationState<A, E> = AsyncResult.AsyncResult<A, E>;

/** 重复提交时的 typed outcome（绝不返回永不 settle 的 Promise） */
export type MutationDropped = { readonly _tag: "Dropped" };

export type MutationOutcome<A> =
  | {
      readonly _tag: "Executed";
      /** Narrow Promise terminal for form adapters; the atom remains state SSOT. */
      readonly completion: Promise<A>;
    }
  | MutationDropped;

type MutationProgram<A, E> = Effect.Effect<A, E, Scope.Scope>;
type RunnerAtom<A, E> = Atom.Writable<AsyncResult.AsyncResult<A, E>, MutationProgram<A, E>>;

export type MutationCommand<A, E> = {
  /** 当前状态（Initial 起始；由 execute 驱动 Waiting/Success/Failure） */
  readonly state: RunnerAtom<A, E>;
  /** 提交一个 Effect mutation；运行中重复提交 drop 并返回 Dropped */
  readonly execute: (program: MutationProgram<A, E>) => MutationOutcome<A>;
  /** 重置回 Initial */
  readonly reset: () => void;
};

export type MutationCommandOptions = {
  /** 重复提交策略：drop（忽略并返回 Dropped；目前唯一策略，显式声明） */
  readonly dedup?: "drop";
};

type CommandInternals = {
  readonly registry: AtomRegistry.AtomRegistry;
  inFlight: boolean;
};

const internals = new WeakMap<MutationCommand<unknown, unknown>, CommandInternals>();

/**
 * 创建一个 Effect-native mutation command。
 *
 * 纯内存状态原语；错误通道 E 原样保留到 AsyncResult.failure，UI 可穷尽处理
 * 而不必 cast 成 Error。运行期以状态订阅判定 in-flight，实现 drop 策略。
 */
export function makeMutationCommand<A, E>(
  _options?: MutationCommandOptions,
): MutationCommand<A, E> {
  const state = Atom.runtime(Layer.empty).fn<E, A, MutationProgram<A, E>>(
    (program) =>
      // Atom.fn 要求 R 包含 Scope/AtomRegistry；registry 在执行期提供这些服务。
      program as unknown as Effect.Effect<
        A,
        E,
        Scope.Scope | AtomRegistry.AtomRegistry | Reactivity.Reactivity
      >,
  ) as unknown as RunnerAtom<A, E>;

  const registry = AtomRegistry.make({});
  registry.mount(state);

  const record: CommandInternals = { registry, inFlight: false };
  registry.subscribe(state, (value) => {
    record.inFlight = AsyncResult.isWaiting(value);
  });

  const command: MutationCommand<A, E> = {
    state,
    execute: (program: MutationProgram<A, E>): MutationOutcome<A> => {
      if (record.inFlight) return { _tag: "Dropped" };

      let resolveCompletion!: (value: A) => void;
      let rejectCompletion!: (cause: unknown) => void;
      const completion = new Promise<A>((resolve, reject) => {
        resolveCompletion = resolve;
        rejectCompletion = reject;
      });
      const tracked = Effect.onExit(Effect.scoped(program), (exit) =>
        Effect.sync(() => {
          if (Exit.isSuccess(exit)) {
            resolveCompletion(exit.value);
          } else {
            rejectCompletion(Cause.squash(exit.cause));
          }
        }),
      );

      // Claim synchronously: immediate duplicate submissions must observe in-flight
      // before AtomRegistry publishes Waiting to subscribers.
      record.inFlight = true;
      registry.set(state, tracked);
      return {
        _tag: "Executed",
        completion,
      };
    },
    reset: () => {
      record.inFlight = false;
      registry.refresh(state);
    },
  };

  internals.set(command as MutationCommand<unknown, unknown>, record);
  return command;
}

/** 测试/宿主直接读取当前状态（不经过 React hook；共享命令私有 registry）。 */
export function readMutationState<A, E>(command: MutationCommand<A, E>): MutationState<A, E> {
  const record = internals.get(command as MutationCommand<unknown, unknown>);
  if (!record) return AsyncResult.initial<A, E>();
  return record.registry.get(command.state);
}

/** Subscribe React/host consumers to the command's own registry. */
export function subscribeMutationState<A, E>(
  command: MutationCommand<A, E>,
  listener: () => void,
): () => void {
  const record = internals.get(command as MutationCommand<unknown, unknown>);
  if (!record) return () => undefined;
  return record.registry.subscribe(command.state, listener);
}
